<template>
  <div class="row">
    <div class="col-12">
      <page-header />
      <div class="row">
        <div class="col-12 col-lg-6 mb-4">
          <div class="card-container">
            <div class="row">
              <div class="col-12 text-center today-container">
                <span>{{ 'total_revenue_today' | i18n }}: </span><span class="grey-text">{{ dashboard.sales.today | formatPrice('eur') }}</span>
              </div>
            </div>
            <div class="row mt-2 mb-2">
              <div class="col-12 col-xl-6 text-center yesterday-container">
                <span>{{ 'yesterday' | i18n }}: </span>
                <span class="grey-text">
                  {{ dashboard.sales.yesterday | formatPrice('eur') }}
                </span>
              </div>
              <div class="col-12 col-xl-6 text-center">
                <span>{{ 'two_days_ago' | i18n }}: </span>
                <span class="grey-text">
                  {{ dashboard.sales.beforeYesterday | formatPrice('eur') }}
                </span>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <bar-chart v-if="isLoaded" :chart-data.sync="chartData.sales" :options="salesChartOptions" :styles="chartStyles" />
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-6 mb-4">
          <div class="card-container">
            <div class="row">
              <div class="col-12 text-center today-container">
                <span>{{ 'number_of_orders_today' | i18n }}: </span>
                <span class="grey-text">
                  {{ dashboard.orderStats.ordersToday }}
                </span>
              </div>
            </div>
            <div class="row mt-2 mb-2">
              <div class="col-12 col-xl-6 text-center yesterday-container">
                <span>{{ 'yesterday' | i18n }}: </span>
                <span class="grey-text">
                  {{ dashboard.orderStats.ordersYesterday }}
                </span>
              </div>
              <div class="col-12 col-xl-6 text-center">
                <span>{{ 'two_days_ago' | i18n }}: </span>
                <span class="grey-text">
                  {{ dashboard.orderStats.ordersBeforeYesterday }}
                </span>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <bar-chart v-if="isLoaded" :chart-data.sync="chartData.orderStats" :options="orderStatsChartOptions" :styles="chartStyles" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-xl-8 mb-4">
          <div class="main-content">
            <latest-orders :items="dashboard.latestOrders" :busy.sync="isLoading" />
          </div>
        </div>
        <div class="col-12 col-xl-4 mb-4">
          <latest-app-contacts :items="dashboard.latestAppContacts" :busy.sync="isLoading" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DashboardMixin, NotificationMixin } from '../../../../mixins';
import LatestOrders from './latest-orders';
import LatestAppContacts from './latest-app-contacts';
import BarChart from './bar-chart';
import PageHeader from '../../../../v2/components/common/page-header';

export default {
  name: 'Dashboard',
  components: { PageHeader, BarChart, LatestAppContacts, LatestOrders },
  mixins: [DashboardMixin, NotificationMixin],
  data: function () {
    return {
      dashboard: {
        latestOrder: null,
        latestOrders: [],
        orderStats: {
          ordersToday: 0,
          ordersYesterday: 0,
          ordersBeforeYesterday: 0,
        },
        sales: {
          today: 0,
          yesterday: 0,
          beforeYesterday: 0,
        },
        latestAppContacts: [],
      },
      chartData: {
        sales: {
          labels: [this.$translate('two_days_ago'), this.$translate('yesterday'), this.$translate('today')],
          datasets: [],
        },
        orderStats: {
          labels: [this.$translate('two_days_ago'), this.$translate('yesterday'), this.$translate('today')],
          datasets: [],
        },
      },
      commonChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        defaultFontFamily: '"Rubik", sans-serif',
      },
      salesChartOptions: {
        scales: {
          yAxes: [
            {
              ticks: {
                stepSize: 100,
                beginAtZero: true,
              },
            },
          ],
        },
      },
      orderStatsChartOptions: {
        scales: {
          yAxes: [
            {
              ticks: {
                stepSize: 10,
                beginAtZero: true,
              },
            },
          ],
        },
      },
      chartStyles: {
        height: '200px',
      },
      orderFields: [
        { key: 'orderDefaultNumber', label: this.$translate('number_short') },
        { key: 'customerNumber', label: this.$translate('customer_number') },
        { key: 'name1', label: this.$translate('name1') },
        { key: 'orderSum', label: this.$translate('sum') },
        { key: 'currency', label: this.$translate('currency') },
        { key: 'createdDate', label: this.$translate('created_date') },
        { key: 'information1', label: this.$translate('info') },
        { key: 'details', label: '' },
      ],
      isLoading: false,
      isLoaded: false,
    };
  },
  created() {
    this.loadDashboard();
  },
  methods: {
    async loadDashboard() {
      try {
        this.isLoading = true;
        const response = await this.dashboardService.getDashboard();
        this.isLoading = false;
        this.dashboard = response.dashboard;
        this.chartData.sales.datasets = [
          {
            label: this.$translate('total_revenue'),
            backgroundColor: '#2260ff',
            barPercentage: 0.4,
            data: [
              Math.round(this.dashboard.sales.beforeYesterday * 100) / 100,
              Math.round(this.dashboard.sales.yesterday * 100) / 100,
              Math.round(this.dashboard.sales.today * 100) / 100,
            ],
          },
        ];
        this.chartData.orderStats.datasets = [
          {
            label: this.$translate('number_of_orders'),
            backgroundColor: '#2260ff',
            barPercentage: 0.4,
            data: [this.dashboard.orderStats.ordersBeforeYesterday, this.dashboard.orderStats.ordersYesterday, this.dashboard.orderStats.ordersToday],
          },
        ];
        this.salesChartOptions = { ...this.commonChartOptions, ...this.salesChartOptions };
        this.orderStatsChartOptions = { ...this.commonChartOptions, ...this.orderStatsChartOptions };
        this.isLoaded = true;
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.label,
.description {
  color: #7586a5;
}

.error {
  color: red;
}

.section-header {
  margin: 0;
}

h5 {
  margin-bottom: 20px;
}

button {
  width: 170px;
}

@media (max-width: 768px) {
  button {
    margin-top: 20px;
  }
}

@media (min-width: 768px) {
  button {
    float: right;
  }
}

.main-content {
  height: 100%;
  min-height: fit-content;
}

.today-container {
  border-bottom: 1px solid lightgrey;
  padding-bottom: 5px;
}

.yesterday-container {
  border-right: 1px solid lightgrey;
}

p {
  margin-bottom: 0.5rem;
}

.grey-text {
  color: #7586a5;
}

.card-bottom {
  position: absolute;
  bottom: 20px;
  right: 30px;
}

.card-container {
  background: #ffffff;
  padding: 15px;
  height: 100%;
  min-height: 130px;
  font-size: 0.9rem;
}
</style>
